/**
 * useMutation hooks for the Publication List write path (create + delete).
 *
 * Replaces `publicationListEffects.createPublication` and
 * `publicationListEffects.deletePublicationById` (compose-app).
 *
 * Auth: stories service REST uses cookie-based session auth via
 * `postJson` (`credentials: 'same-origin'`). Per
 * COMPOSE_RETIREMENT_GUIDELINES.md §1, do NOT use
 * `useDynamicFetchMutation` from `@opengov/bnp-core` here — it sends
 * Auth0 Bearer tokens which the stories service rejects.
 *
 * Cache: each mutation invalidates the broad list key
 * `[...publicationListQueryKeys.all, 'list']` on success so every
 * filter/pagination variant currently in cache refetches.
 *
 * Toasts: errors go through `useToast` (§8). The caller passes copy in.
 *
 * Admin guard (previously inside `createPublication` effect) is the
 * caller's responsibility — the hook just does the API call.
 *
 * Clone publication: the Copy flow is two steps — call
 * `useCreatePublication` for the empty target, then
 * `publicationViewEffects.clonePublication` (still compose-app) to copy
 * content over. Bridge is intentional until that effect is retired.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UNIQUENESS_VIOLATION } from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';

import { postJson } from '../../../../utils/fetchUtils';
import { useToast } from '../../../Toast/context/useToast.context';

import { publicationListQueryKeys } from './publicationListQueryKeys';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type ToastCopy = {
  title: string;
  body?: string;
};

// ---------------------------------------------------------------------------
// Create publication
// ---------------------------------------------------------------------------

export type CreatePublicationInput = {
  title: string;
  description?: string;
  entityId: number;
  entitySubdomain: string;
  folderId?: string;
  createSubjectClaim?: boolean;
};

type CreatePublicationResponse = {
  publication: IPublication;
};

/**
 * Caller checks admin before calling. The hook doesn't gate on `isAdmin`.
 *
 * Errors:
 * - Server returns the string "Uniqueness violation." when a publication
 *   with the same title already exists. We re-throw so callers can show
 *   inline validation (e.g. CreatePublicationModal sets `hasSameName`).
 *   Toast suppressed in that case.
 * - All other errors → toast.
 */
export function useCreatePublication(errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<IPublication, Error, CreatePublicationInput>({
    mutationKey: publicationListQueryKeys.create(),
    mutationFn: async (input) => {
      const { title, description, entityId, entitySubdomain, folderId, createSubjectClaim } = input;
      const data = await postJson<CreatePublicationResponse>('/api/stories/v1/publications', {
        publication: {
          entityId,
          title,
          description,
          status: 'draft',
          thumbnailPath: ''
        },
        folderId,
        entitySubdomain,
        createSubjectClaim
      });
      return data.publication;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...publicationListQueryKeys.all, 'list']
      });
    },
    onError: (error) => {
      if (error?.message?.includes(UNIQUENESS_VIOLATION)) {
        // Don't toast on duplicate title — caller shows inline error.
        return;
      }
      toggleToast({
        title: errorToast.title,
        body: errorToast.body,
        severity: 'error'
      });
    }
  });
}

// ---------------------------------------------------------------------------
// Delete publication
// ---------------------------------------------------------------------------

export type DeletePublicationInput = {
  publicationId: string;
  publicationName: string;
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
  publishedId?: string;
};

/**
 * Success toast copy is built by the caller (uses publication name in
 * the body), so both `successToast` and `errorToast` are passed in.
 *
 * Navigation back to the list is the caller's responsibility — the
 * delete modal can be opened from either the list page (no nav needed)
 * or the publication view page (must nav back).
 */
export function useDeletePublication(successToast: ToastCopy, errorToast: ToastCopy) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<unknown, Error, DeletePublicationInput>({
    mutationKey: publicationListQueryKeys.delete(),
    mutationFn: ({
      publicationId,
      publicationName,
      entityId,
      entityUuid,
      entitySubdomain,
      publishedId
    }) =>
      postJson(`/api/stories/v1/publications/${publicationId}/delete`, {
        publication_id: publicationId,
        entity_id: entityId,
        entity_uuid: entityUuid,
        entity_subdomain: entitySubdomain,
        published_id: publishedId,
        publication_title: publicationName
      }),
    onSuccess: () => {
      toggleToast({
        title: successToast.title,
        body: successToast.body,
        severity: 'success'
      });
      void queryClient.invalidateQueries({
        queryKey: [...publicationListQueryKeys.all, 'list']
      });
    },
    onError: () => {
      toggleToast({
        title: errorToast.title,
        body: errorToast.body,
        severity: 'error'
      });
    }
  });
}
